// P1 — a body rewrite must replace ONLY the TERMINAL identifier component of
// the reference the index recorded, in the delimiter style that component was
// written in.
//
// The index does not record one token per reference. `index/columnRefScanner.ts`'s
// `emitAliasQualifiedColumnRefs` stores the whole `<alias>.<column>` chain
// (`qn.range`), and MySQL's structural select-list walker stores the whole
// select item (`` `u`.`email` AS `email` ``). Planning the replacement over that
// span dropped everything but the new bare name:
//
//   `SELECT u.id … JOIN accounts a ON a.user_id = u.id`   rename users.id → user_id
//     before → `SELECT user_id … ON a.user_id = user_id`  ← ambiguous, two relations own it
//     after  → `SELECT u.user_id … ON a.user_id = u.user_id`
//
//   `` `u`.`email` ``  rename email → email_address
//     before → `email_address`            ← delimiters lost
//     after  → `` `u`.`email_address` ``
//
// The narrowing reuses the resolver's own identifier scanner
// (`bufferIdentifierScan.forEachIdentifierChain`, which already reads each
// engine's delimiters and skips comments / string literals) and the DDL
// synthesizers' own `quoteWithStyle`, so the per-engine delimiter table stays in
// one place. A new name that cannot stand unquoted (`user id`, `1st`, `select`)
// is quoted with the engine helper those synthesizers use (`needsQuoting` +
// `maybeQuoteIdent`).
//
// P1 (follow-up) — PRESERVING THE DELIMITERS IS ONLY HALF THE JOB. Inside
// delimiters an identifier's identity is its exact bytes, so the token has to
// spell the name the SCHEMA rename actually created, not the one the user typed
// into the rename box. Renaming Oracle's ordinary `USERS.ID` to `user_id` emits
// an UNQUOTED `ALTER TABLE users RENAME COLUMN ID TO user_id`, which creates
// `USER_ID`; rewriting a valid `u."ID"` reference to `u."user_id"` then names a
// column that does not exist and leaves the dependent view invalid (ORA-00904).
// `renameDestination.ts` derives that spelling ONCE per plan, from the
// statements `renameSchemaAlter.ts` emits — see its table. This module is handed
// the answer; it never asks the question itself.

import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { IdentifierQuoting, ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { maybeQuoteIdent } from '../../sql-resolver/ddl/apply/dialect';
import { foldAsStored, needsQuoting, quoteWithStyle } from '../../sql-resolver/ddl/apply/dialect.quoting';
import type { PlannedByteEdit } from '../../sql-resolver/types';

import type { SqlRange } from '../types';
import { forEachIdentifierChain } from './bufferIdentifierScan';

export type TerminalTokenEditInput = {
  /** The object body the recorded ranges index into. */
  readonly body: string;
  /** Line index of `body` — built once per body by the caller. */
  readonly lineIndex: LineIndex;
  /** The reference range exactly as the index recorded it. */
  readonly range: SqlRange;
  /** The old identifier as the rename request spells it — used to pick the
   *  right token when the recorded range spans more than one (a wrapped select
   *  item, an `AS <alias>` tail). */
  readonly fromName: string;
  /** The new identifier's own bytes — the rename box's text with any
   *  delimiters the user typed already decoded off it (`renameDestination.ts`).
   */
  readonly newName: string;
  /** The spelling the CATALOG holds once the schema-side rename has run — the
   *  only identity a body rewrite may name. Read off the plan's
   *  `RenameDestination` (`renameDestination.ts`); equal to `newName` on every
   *  engine × kind whose schema rename delimits (or literalises) its
   *  destination. */
  readonly catalogName: string;
  readonly engine: SqlEngine;
  readonly rationale: string;
};

/** Build the one byte edit this reference calls for: the terminal component of
 *  the identifier chain inside `range`, replaced by `newName` in that
 *  component's own delimiter style.
 *
 *  Falls back to the recorded range (the pre-P1 behaviour) when no identifier
 *  in the range carries the old name — the range then describes something this
 *  module cannot narrow, and widening the blast radius further would be worse
 *  than leaving the historical behaviour. The token it writes there still has
 *  to denote `catalogName`, so it goes through the same renderer as a bare
 *  terminal. */
export function planTerminalTokenEdit(input: TerminalTokenEditInput): PlannedByteEdit {
  const options: ParseOptions = { engine: input.engine };
  const terminal = findTerminalToken(input.body, input.range, input.fromName, options);
  // The un-narrowable fallback still replaces a whole recorded range with ONE
  // token, so it answers the same identity question a bare terminal does.
  const quoting: IdentifierQuoting = terminal?.quoting ?? 'unquoted';
  const replacement = renderCatalogToken(input.newName, input.catalogName, quoting, input.engine);
  if (!terminal) {
    return { range: input.range, replacement, rationale: input.rationale };
  }
  return {
    range: rangeFromOffsets(input.lineIndex, terminal.start, terminal.end),
    replacement,
    rationale: input.rationale,
  };
}

export { buildLineIndex };

type TerminalToken = {
  readonly start: number;
  readonly end: number;
  readonly value: string;
  readonly quoting: IdentifierQuoting;
};

/** The terminal component of the identifier chain inside `range` that names
 *  `fromName`. An exact spelling wins over a case-insensitive one so a
 *  case-sensitive catalog (`SELECT t.Id, t.id`) picks the token the record is
 *  actually about; the case-insensitive pass covers the engines whose catalog
 *  spelling and body spelling legitimately differ. */
function findTerminalToken(
  body: string,
  range: SqlRange,
  fromName: string,
  options: ParseOptions,
): TerminalToken | null {
  const candidates: TerminalToken[] = [];
  forEachIdentifierChain(body, options, range.startOffset, range.endOffset, (chain) => {
    candidates.push({
      start: chain.terminal.start,
      end: chain.terminal.end,
      // `qualified.name` is the LAST part's unquoted value — the column /
      // relation leaf, never the `AS <alias>` that may follow it in the range.
      value: chain.qualified.name,
      quoting: chain.terminalQuoting,
    });
  });
  const lower = fromName.toLowerCase();
  return (
    candidates.find((c) => c.value === fromName) ??
    candidates.find((c) => c.value.toLowerCase() === lower) ??
    null
  );
}

/** The replacement token for a reference that was written with `quoting`.
 *
 *  Whatever it renders must DENOTE `catalogName` — the name the schema rename
 *  created — because that is the only column/relation the rewritten body can
 *  resolve:
 *
 *    • delimited reference → `catalogName` verbatim inside the same delimiters.
 *      Delimiters suppress folding, so the bytes ARE the identity: keeping the
 *      typed `user_id` inside Oracle's quotes would name a column the
 *      `USER_ID`-creating ALTER never made.
 *    • bare reference → the typed name, bare, WHEN the engine folds it to
 *      `catalogName` (the overwhelmingly common case: the body token and the
 *      ALTER's target are folded by the same rules). Otherwise — a quoted ALTER
 *      that kept a mixed-case spelling, or a name that cannot stand undelimited
 *      (`user id`, `1st`) — the engine's default delimiters go on around
 *      `catalogName`, via the same `needsQuoting` + `maybeQuoteIdent` pair
 *      `dialect.tableLifecycle.ts` uses.
 *
 *  `newName` is the identifier's own bytes: a spelling the user typed WITH
 *  delimiters lost them once already, at the entry point
 *  (`decodeDelimitedIdentifier`), so there is no verbatim-passthrough case left
 *  here — a destination that was typed delimited simply has a `catalogName` a
 *  bare token cannot fold to, and falls into the last branch.
 *
 *  That is also why the delimited branch goes through `quoteWithStyle` rather
 *  than `bufferIdentifierScan.ts`'s `renderRenamedToken`: the latter takes the
 *  text a user TYPED and passes an already-delimited spelling through
 *  untouched, which is right for the buffer-local rename that still hands it
 *  raw input — and wrong for a name that has already been decoded. Decoded
 *  bytes may THEMSELVES begin with a delimiter character: PostgreSQL
 *  `"""next"""` in the box denotes a column literally named `"next"` (and
 *  `[next]` on Oracle, which accepts no `"` inside an identifier at all,
 *  ORA-01741). Passed through, an open `u."ID"` became `u."next"` — a
 *  different column — instead of the escaped `u."""next"""`.
 *
 *  Exported because the OPEN-TAB rewrite (`tabRewritePlanner.ts`) answers the
 *  same question about a live buffer and must not grow a second copy of the
 *  fold table. */
export function renderCatalogToken(
  newName: string,
  catalogName: string,
  quoting: IdentifierQuoting,
  engine: SqlEngine,
): string {
  if (quoting !== 'unquoted') return quoteWithStyle(catalogName, quoting);
  // …and the same engine-aware question `renameDestination.ts` asked when it
  // derived `catalogName`, for the same reason: a `#` name has no bare spelling on PostgreSQL, MySQL
  // or SQLite, so the token that denotes `catalogName` there is a delimited one
  // whatever the fold says.
  if (!needsQuoting(newName, engine) && foldAsStored(newName, engine) === catalogName) {
    return newName;
  }
  return maybeQuoteIdent(engine, catalogName, true);
}
